// /src/pages/InvoiceReviewPage.js
// Invoice Agent — review and approve parsed invoices.
//
// The queue is everything that has not yet reached its system of record. Open
// one and you get the scan beside the values read off it: correct anything that
// is wrong, watch the proposed Sage 50 posting move with your correction, then
// approve and post.
//
// Nothing here decides whether an invoice is postable. The server recomputes
// validation and the Sage mapping on every read and every edit, and refuses to
// approve or post an invoice that is not clean. This screen shows that verdict;
// it never forms its own.
import React, { useCallback, useEffect, useRef, useState } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    const raw = await res.text();
    let parsed = null;
    try { parsed = raw ? JSON.parse(raw) : null; } catch { /* keep raw */ }
    if (!res.ok) {
      const err = new Error(parsed?.error || raw || `${res.status} ${res.statusText}`);
      err.status = res.status;
      err.body = parsed;
      throw err;
    }
    return parsed;
  });
}

// The scan image needs the Authorization header, which a plain <img src> does
// not send -- it would arrive unauthenticated and render as a broken image.
async function fetchAuthedImage(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return URL.createObjectURL(await res.blob());
}

const money = (v) =>
  v === null || v === undefined || v === "" ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR = {
  parsed: "#6e7781", in_review: "#9a6700", approved: "#1a7f37",
  posted: "#0969da", rejected: "#cf222e",
};

const box = { border: "1px solid #30363d", borderRadius: 8, padding: 14, marginBottom: 14 };
const label = { fontSize: 11, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6 };

// ---------------------------------------------------------------------------
// One editable value. Saves on blur rather than on every keystroke: each save
// re-runs the Sage mapping server-side, and doing that per character would be
// both slow and a poor audit trail.
// ---------------------------------------------------------------------------
function Field({ path, value, onSave, disabled, width = 170 }) {
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = async () => {
    const next = draft === "" ? null : draft;
    if (String(next ?? "") === String(value ?? "")) return;
    setBusy(true);
    try { await onSave(path, next); } finally { setBusy(false); }
  };

  return (
    <input
      value={draft}
      disabled={disabled || busy}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      style={{
        width, padding: "5px 7px", background: disabled ? "transparent" : "#0d1117",
        color: "inherit", border: "1px solid #30363d", borderRadius: 5,
        opacity: busy ? 0.5 : 1,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
export default function InvoiceReviewPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [openId, setOpenId] = useState(null);
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPosted, setShowPosted] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      setErr("");
      const q = showPosted ? "" : "&open=1";
      const data = await api(`/documents?doc_type=invoice${q}`);
      setRows(data.documents || []);
      setCounts(data.counts || {});
    } catch (e) { setErr(e.message); }
  }, [showPosted]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openDoc = async (id) => {
    setErr(""); setNote(""); setOpenId(id); setDoc(null);
    try { setDoc(await api(`/documents/${id}`)); }
    catch (e) { setErr(e.message); }
  };

  const saveField = async (path, value) => {
    try {
      setErr("");
      const res = await api(`/documents/${openId}`, {
        method: "PATCH", body: { changes: { [path]: value } },
      });
      // Everything downstream is recomputed server-side, so take the whole
      // invoice back rather than patching it locally and drifting.
      setDoc((d) => ({ ...d, invoice: res.invoice, edit_history: res.edit_history }));
      loadQueue();
    } catch (e) { setErr(e.message); }
  };

  const act = async (verb, opts = {}) => {
    setBusy(true); setErr(""); setNote("");
    try {
      const res = await api(`/documents/${openId}/${verb}${opts.query || ""}`, {
        method: "POST", body: opts.body,
      });
      if (verb === "post" && res.status === "dry_run_ok") {
        const w = res.would_post || {};
        setNote(`Dry run OK — would post as PostOrder ${w.PostOrder}, `
              + `transaction ${w.JrnlKey_TrxNumber}, period ${w.JrnlKey_Per}, `
              + `${w.rows} rows. Nothing was written.`);
      } else if (verb === "post") {
        const w = res.post_reference || {};
        setNote(`Posted to Sage 50 as PostOrder ${w.PostOrder} (transaction ${w.JrnlKey_TrxNumber}).`);
      } else {
        setNote(`Invoice ${res.status}.`);
      }
      await openDoc(openId);
      await loadQueue();
    } catch (e) {
      // The server returns its blockers with a refusal; showing them is more
      // useful than the one-line message on its own.
      const blockers = e.body?.blockers;
      setErr(blockers?.length ? `${e.message} — ${blockers.join("; ")}` : e.message);
    } finally { setBusy(false); }
  };

  if (openId && doc) {
    return (
      <Detail
        doc={doc} err={err} note={note} busy={busy}
        onBack={() => { setOpenId(null); setDoc(null); setNote(""); setErr(""); loadQueue(); }}
        onSave={saveField} onAct={act}
      />
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <h2>Invoice review</h2>
      <p style={{ opacity: 0.75, marginTop: -6, maxWidth: 760 }}>
        Invoices read from uploaded scans, waiting to be checked and posted to Sage 50.
        Approving is blocked until the invoice ties out and its vendor and items resolve.
      </p>

      <div style={{ display: "flex", gap: 14, alignItems: "center", margin: "12px 0" }}>
        {Object.entries(counts).map(([k, v]) => (
          <span key={k} style={{ fontSize: 13 }}>
            <strong style={{ color: STATUS_COLOR[k] || "inherit" }}>{v}</strong>{" "}
            <span style={{ opacity: 0.7 }}>{k.replace("_", " ")}</span>
          </span>
        ))}
        <label style={{ fontSize: 13, opacity: 0.75, cursor: "pointer", marginLeft: "auto" }}>
          <input type="checkbox" checked={showPosted}
                 onChange={(e) => setShowPosted(e.target.checked)} style={{ marginRight: 6 }} />
          Include posted
        </label>
      </div>

      {err && <div style={{ ...box, borderColor: "#cf222e", color: "#ff7b72" }}>{err}</div>}

      {rows.length === 0 ? (
        <p style={{ opacity: 0.6 }}>Nothing waiting for review.</p>
      ) : (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #30363d" }}>
              <th style={{ padding: "6px 8px" }}>Vendor</th>
              <th style={{ padding: "6px 8px" }}>Invoice</th>
              <th style={{ padding: "6px 8px" }}>Date</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Total</th>
              <th style={{ padding: "6px 8px" }}>Status</th>
              <th style={{ padding: "6px 8px" }}>Checks</th>
              <th style={{ padding: "6px 8px" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid #21262d" }}>
                <td style={{ padding: "6px 8px" }}>
                  {r.party_name || <em style={{ opacity: 0.6 }}>unknown</em>}
                  {r.party_record_number ? (
                    <span style={{ opacity: 0.5, fontSize: 12 }}> #{r.party_record_number}</span>
                  ) : null}
                </td>
                <td style={{ padding: "6px 8px" }}>{r.reference || "—"}</td>
                <td style={{ padding: "6px 8px" }}>{r.doc_date || "—"}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{money(r.amount)}</td>
                <td style={{ padding: "6px 8px", color: STATUS_COLOR[r.status], fontWeight: 600 }}>
                  {r.status.replace("_", " ")}
                  {r.edited && <span style={{ opacity: 0.6, fontWeight: 400 }}> · edited</span>}
                </td>
                <td style={{ padding: "6px 8px" }}>
                  {r.ready_to_post
                    ? <span style={{ color: "#3fb950" }}>ready</span>
                    : <span style={{ color: "#d29922" }}>{r.blocker_count} blocker{r.blocker_count === 1 ? "" : "s"}</span>}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>
                  <button onClick={() => openDoc(r.id)}>Review</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The review screen proper: the scan on the left, what was read from it on the
// right, and the posting it would produce underneath.
// ---------------------------------------------------------------------------
function Detail({ doc, err, note, busy, onBack, onSave, onAct }) {
  const inv = doc.invoice || {};
  const sage = inv.sage50 || {};
  const val = inv.validation || {};
  const vendor = inv.vendor || {};
  const resolution = sage.resolution || {};
  const locked = doc.status === "posted";

  const [page, setPage] = useState(1);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgErr, setImgErr] = useState("");
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const release = () => {
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
    release(); setImgUrl(null); setImgErr("");
    fetchAuthedImage(`/api/documents/${doc.id}/page/${page}`)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        urlRef.current = u; setImgUrl(u);
      })
      .catch((e) => { if (!cancelled) setImgErr(e.message); });
    return () => { cancelled = true; release(); };
  }, [doc.id, page]);

  const editedPaths = new Set(Object.keys(doc.edits || {}));
  const mark = (path) => (editedPaths.has(path)
    ? { borderLeft: "3px solid #d29922", paddingLeft: 6 } : null);

  return (
    <div style={{ padding: 16 }}>
      <button onClick={onBack}>&larr; Back to queue</button>

      <div style={{ display: "flex", alignItems: "baseline", gap: 14, margin: "12px 0" }}>
        <h2 style={{ margin: 0 }}>{vendor.name || "Unknown vendor"} — {inv.invoice_number || "no number"}</h2>
        <span style={{ color: STATUS_COLOR[doc.status], fontWeight: 600 }}>
          {doc.status.replace("_", " ")}
        </span>
        {locked && <span style={{ opacity: 0.7, fontSize: 13 }}>· posted, read only</span>}
      </div>

      {err && <div style={{ ...box, borderColor: "#cf222e", color: "#ff7b72" }}>{err}</div>}
      {note && <div style={{ ...box, borderColor: "#1a7f37", color: "#3fb950" }}>{note}</div>}

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        {/* ---- the scan ---- */}
        <div style={{ flex: "0 0 460px", position: "sticky", top: 12 }}>
          <div style={{ ...box, padding: 8 }}>
            {imgErr ? (
              <div style={{ opacity: 0.7, padding: 12 }}>Scan not available ({imgErr})</div>
            ) : imgUrl ? (
              <img src={imgUrl} alt={`Page ${page}`}
                   style={{ width: "100%", borderRadius: 4, display: "block" }} />
            ) : (
              <div style={{ opacity: 0.6, padding: 12 }}>Loading page…</div>
            )}
            {(doc.page_count || 1) > 1 && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "center" }}>
                {Array.from({ length: doc.page_count }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setPage(n)}
                          style={{ fontWeight: n === page ? 700 : 400 }}>{n}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---- what was read ---- */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {(sage.blockers || []).length > 0 && (
            <div style={{ ...box, borderColor: "#d29922" }}>
              <div style={{ ...label, color: "#d29922" }}>Blocking approval</div>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {sage.blockers.map((b, i) => <li key={i} style={{ marginBottom: 4 }}>{b}</li>)}
              </ul>
            </div>
          )}
          {(sage.warnings || []).length > 0 && (
            <div style={{ ...box }}>
              <div style={label}>Worth a look</div>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, opacity: 0.85 }}>
                {sage.warnings.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
              </ul>
            </div>
          )}

          <div style={box}>
            <div style={label}>Vendor</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "8px 0" }}>
              <span style={mark("vendor.name")}>
                <Field path="vendor.name" value={vendor.name} onSave={onSave}
                       disabled={locked} width={260} />
              </span>
              {resolution.vendor?.matched ? (
                <span style={{ color: "#3fb950", fontSize: 13 }}>
                  → {resolution.vendor.VendorID} #{resolution.vendor.VendorRecordNumber}
                  <span style={{ opacity: 0.6 }}> ({resolution.vendor.method})</span>
                </span>
              ) : (
                <span style={{ color: "#d29922", fontSize: 13 }}>not matched in Sage 50</span>
              )}
            </div>
          </div>

          <div style={box}>
            <div style={label}>Invoice</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, auto)", gap: 10, marginTop: 8 }}>
              {[["invoice_number", "Number"], ["invoice_date", "Date"], ["due_date", "Due"],
                ["customer_po", "PO"], ["terms", "Terms"], ["ship_via", "Ship via"]].map(([p, t]) => (
                <div key={p} style={mark(p)}>
                  <div style={{ ...label, marginBottom: 3 }}>{t}</div>
                  <Field path={p} value={inv[p]} onSave={onSave} disabled={locked} width={150} />
                </div>
              ))}
            </div>
          </div>

          <div style={box}>
            <div style={label}>Lines</div>
            <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 8 }}>
              <thead>
                <tr style={{ textAlign: "left", opacity: 0.6, fontSize: 12 }}>
                  <th style={{ padding: 4 }}>Part</th>
                  <th style={{ padding: 4 }}>Description</th>
                  <th style={{ padding: 4, textAlign: "right" }}>Qty</th>
                  <th style={{ padding: 4, textAlign: "right" }}>Unit</th>
                  <th style={{ padding: 4, textAlign: "right" }}>Extended</th>
                  <th style={{ padding: 4 }}>Sage item</th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((ln, i) => {
                  const item = (resolution.items || []).find((x) => x.line_no === ln.line_no);
                  return (
                    <tr key={i} style={{ borderTop: "1px solid #21262d" }}>
                      <td style={{ padding: 4, ...mark(`lines.${i}.part_number`) }}>
                        <Field path={`lines.${i}.part_number`} value={ln.part_number}
                               onSave={onSave} disabled={locked} width={130} />
                      </td>
                      <td style={{ padding: 4, ...mark(`lines.${i}.description`) }}>
                        <Field path={`lines.${i}.description`} value={ln.description}
                               onSave={onSave} disabled={locked} width={210} />
                      </td>
                      <td style={{ padding: 4, ...mark(`lines.${i}.quantity`) }}>
                        <Field path={`lines.${i}.quantity`} value={ln.quantity}
                               onSave={onSave} disabled={locked} width={70} />
                      </td>
                      <td style={{ padding: 4, ...mark(`lines.${i}.unit_price`) }}>
                        <Field path={`lines.${i}.unit_price`} value={ln.unit_price}
                               onSave={onSave} disabled={locked} width={80} />
                      </td>
                      <td style={{ padding: 4, textAlign: "right",
                                   color: ln.extended_ok === false ? "#d29922" : "inherit" }}
                          title={ln.extended_ok === false
                            ? `qty x unit = ${money(ln.extended_computed)} on the page it says ${money(ln.extended)}`
                            : undefined}>
                        {money(ln.extended)}
                      </td>
                      <td style={{ padding: 4, fontSize: 12 }}>
                        {item?.matched
                          ? <span style={{ color: "#3fb950" }}>#{item.ItemRecordNumber} {item.ItemID}</span>
                          : <span style={{ color: "#d29922" }}>no match</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: "flex", gap: 18, justifyContent: "flex-end", marginTop: 12 }}>
              {[["subtotal", "Subtotal"], ["freight", "Freight"], ["sales_tax", "Tax"],
                ["total_due", "Total"]].map(([p, t]) => (
                <div key={p} style={{ textAlign: "right", ...mark(p) }}>
                  <div style={{ ...label, marginBottom: 3 }}>{t}</div>
                  <Field path={p} value={inv[p]} onSave={onSave} disabled={locked} width={110} />
                </div>
              ))}
            </div>
          </div>

          {/* ---- the posting this produces ---- */}
          <div style={box}>
            <div style={label}>
              Proposed Sage 50 posting — journal {sage.journal?.JrnlKey_Journal}, module {sage.journal?.Module}
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 8, fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", opacity: 0.6, fontSize: 12 }}>
                  <th style={{ padding: 4 }}>#</th>
                  <th style={{ padding: 4 }}>GL</th>
                  <th style={{ padding: 4 }}>Description</th>
                  <th style={{ padding: 4, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(sage.JRNLROW || []).map((r) => (
                  <tr key={r.RowNumber} style={{ borderTop: "1px solid #21262d" }}>
                    <td style={{ padding: 4 }}>{r.RowNumber}</td>
                    <td style={{ padding: 4 }}>{r.GLAcntNumber}</td>
                    <td style={{ padding: 4 }}>{r.RowDescription}</td>
                    <td style={{ padding: 4, textAlign: "right",
                                 color: r.Amount < 0 ? "#79c0ff" : "inherit" }}>
                      {money(r.Amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: "right", marginTop: 8, fontSize: 13 }}>
              debits {money(sage.balance?.debits)} · credits {money(sage.balance?.credits)}{" "}
              <strong style={{ color: sage.balance?.balanced ? "#3fb950" : "#cf222e" }}>
                {sage.balance?.balanced ? "balanced" : "does not balance"}
              </strong>
            </div>
          </div>

          {doc.post_reference && (
            <div style={{ ...box, borderColor: "#0969da" }}>
              <div style={label}>Posted to Sage 50</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                PostOrder {doc.post_reference.PostOrder} · transaction {doc.post_reference.JrnlKey_TrxNumber}
                {" "}· period {doc.post_reference.JrnlKey_Per} · {doc.post_reference.rows} rows
              </div>
            </div>
          )}

          {(doc.edit_history || []).length > 0 && (
            <details style={box}>
              <summary style={{ cursor: "pointer", ...label }}>
                {doc.edit_history.length} correction{doc.edit_history.length === 1 ? "" : "s"}
              </summary>
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, opacity: 0.85 }}>
                {doc.edit_history.map((h, i) => (
                  <li key={i} style={{ marginBottom: 3 }}>
                    <code>{h.field_path}</code>: {String(h.old_value)} → {String(h.new_value)}
                    <span style={{ opacity: 0.6 }}> · {h.edited_by_email}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {!locked && (
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button disabled={busy || !sage.ready_to_post || doc.status === "approved"}
                      onClick={() => onAct("approve")}
                      title={sage.ready_to_post ? "" : "Resolve the blockers above first"}>
                Approve
              </button>
              <button disabled={busy} onClick={() => onAct("post", { query: "?dry_run=1" })}>
                Dry run against Sage
              </button>
              <button disabled={busy || doc.status !== "approved"}
                      onClick={() => onAct("post")}
                      title={doc.status === "approved" ? "" : "Approve it first"}>
                Post to Sage 50
              </button>
              <button disabled={busy} style={{ marginLeft: "auto" }}
                      onClick={() => {
                        const reason = window.prompt("Why is this invoice being rejected?");
                        if (reason !== null) onAct("reject", { body: { reason } });
                      }}>
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
